// One phone format for every surface (website, dashboards, app). People type a mobile
// number every way there is — "01712345678", "1712 345678", "+880 1712-345678",
// "+88 017…", Bangla digits — and every field takes all of them, shows the same
// "{dial} 1712-345678" and sends the E.164 value the contract wants (common.yaml Phone).
//
// Tenant-blind (INF-1): the dial code and the national-number shape are the market's,
// so they come from each surface's theme/ as a `PhonePlan`; nothing here names a market.
// Phones stay ASCII in both languages (ascii-codes.ts).

export interface PhonePlan {
  /** E.164 country prefix shown as the field's fixed chip, e.g. "+…". */
  readonly dial: string;
  /** Digits of a mobile number after the dial code (the trunk digit excluded). */
  readonly nationalDigits: number;
  /** The local trunk prefix people type before the national number. */
  readonly trunk: string;
  /** Every mobile national number starts with this. */
  readonly mobileLead: string;
  /** Where the national number takes its one hyphen ("1712-345678"). */
  readonly groupAfter: number;
}

const NBSP = ' ';
const BANGLA_ZERO = 0x09e6;

function asciiDigits(raw: string): string {
  return raw.replace(/[০-৯]/g, (d) => String(d.charCodeAt(0) - BANGLA_ZERO)).replace(/\D/g, '');
}

// Every digit typed or pasted, with the dial code and the trunk prefix removed — the
// national number, uncapped.
function nationalOf(raw: string, plan: PhonePlan): string {
  let digits = asciiDigits(raw);
  const dialDigits = plan.dial.replace(/\D/g, '');
  if (dialDigits && digits.startsWith(dialDigits) && !digits.startsWith(plan.mobileLead)) digits = digits.slice(dialDigits.length);
  while (plan.trunk && digits.startsWith(plan.trunk)) digits = digits.slice(plan.trunk.length);
  return digits;
}

/** What a phone field holds after a keystroke or a paste: the national digits, capped. */
export function phoneInputDigits(raw: string, plan: PhonePlan): string {
  return nationalOf(raw, plan).slice(0, plan.nationalDigits);
}

/** National digits as the field shows them while typing: "1712345678" → "1712-345678". */
export function formatPhoneInput(digits: string, plan: PhonePlan): string {
  return digits.length > plan.groupAfter ? `${digits.slice(0, plan.groupAfter)}-${digits.slice(plan.groupAfter)}` : digits;
}

/** The field's placeholder: "1XXX-XXXXXX". */
export function phonePlaceholder(plan: PhonePlan): string {
  return formatPhoneInput(plan.mobileLead + 'X'.repeat(Math.max(0, plan.nationalDigits - plan.mobileLead.length)), plan);
}

/** A complete mobile number: exactly `nationalDigits` digits starting with `mobileLead`. */
export function isValidMobile(raw: string, plan: PhonePlan): boolean {
  const national = nationalOf(raw, plan);
  return national.length === plan.nationalDigits && national.startsWith(plan.mobileLead);
}

/** Any typed form → E.164 ("+…1712345678"), or null when it is not a complete mobile number. */
export function toE164(raw: string, plan: PhonePlan): string | null {
  return isValidMobile(raw, plan) ? `${plan.dial}${nationalOf(raw, plan)}` : null;
}

/** An E.164 (or local) mobile number for display: "{dial} 1712-345678". Anything else is returned unchanged. */
export function formatPhone(value: string | null | undefined, plan: PhonePlan): string {
  if (!value) return '';
  if (!isValidMobile(value, plan)) return value;
  return `${plan.dial}${NBSP}${formatPhoneInput(nationalOf(value, plan), plan)}`;
}

/** A mobile number masked for display: "{dial} 17••-•••678". Anything else is returned unchanged. */
export function maskPhone(value: string | null | undefined, plan: PhonePlan): string {
  if (!value) return '';
  if (!isValidMobile(value, plan)) return value;
  const national = nationalOf(value, plan);
  const shown = national.slice(0, 2) + '•'.repeat(national.length - 5) + national.slice(-3);
  return `${plan.dial}${NBSP}${formatPhoneInput(shown, plan)}`;
}
